import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';

const DIM_LABELS = {
  skills: 'Skills', experience: 'Experience', domain: 'Domain', education: 'Education',
  leadership: 'Leadership', scope_scale: 'Scope/Scale', complexity: 'Complexity',
  business_impact: 'Business Impact', stakeholder: 'Stakeholder',
};
const ROLE_LABELS = { technical: 'Technical / IC', sales: 'Sales / Revenue', leadership: 'Leadership / Exec', general: 'General' };
const DIM_ORDER = Object.keys(DIM_LABELS);
const pct = (v) => `${Math.round((v || 0) * 100)}%`;

function scopeTitle(s) {
  const role = ROLE_LABELS[s.role_type] || s.role_type;
  return s.scope_type === 'company' ? `${s.company_slug} · ${role}` : `All companies · ${role}`;
}

export function LearningTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getLearning();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load learning data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setRunning(true);
    try {
      const res = await adminAPI.recomputeLearning();
      toast.success(`Recomputed — ${res.data.scopes_learned} scope(s) learned from ${res.data.labeled_candidates} labeled candidates`);
      await load();
    } catch (e) {
      toast.error('Recompute failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !data) return <div style={{ color: '#64748B', padding: '2rem' }}>Loading learned weights…</div>;
  if (!data) return null;

  const scopes = data.scopes || [];

  return (
    <div data-testid="learning-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', maxWidth: 620, margin: 0, lineHeight: 1.6 }}>
          The engine learns which of the 9 scoring dimensions best predict who recruiters <strong>advance</strong> vs
          <strong> reject</strong>, then nudges the weights per role (and per company) — blended with the expert presets and
          capped at {pct(data.max_alpha)} trust. A scope needs at least <strong>{data.min_samples}</strong> labeled candidates
          before anything is learned; until then the presets are used.
        </p>
        <Button data-testid="learning-recompute-btn" onClick={recompute} disabled={running} style={{ borderRadius: 8, background: '#F05000', color: '#fff' }}>
          {running ? 'Recomputing…' : 'Recompute now'}
        </Button>
      </div>

      {scopes.length === 0 ? (
        <Card data-testid="learning-empty" style={{ background: 'hsl(var(--card))', border: '1px dashed hsl(var(--border))', borderRadius: 14, padding: '2.5rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'Cabinet Grotesk', marginBottom: '0.5rem' }}>No learned weights yet</div>
          <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
            The engine is still using the expert role presets. Once a role accumulates {data.min_samples}+ advanced/rejected
            decisions, click <strong>Recompute now</strong> and its learned weights will appear here.
          </p>
        </Card>
      ) : (
        scopes.map((s) => (
          <Card key={`${s.scope_type}-${s.company_slug || ''}-${s.role_type}`} data-testid="learning-scope-card"
            style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 700 }}>{scopeTitle(s)}</div>
                <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.15rem' }}>
                  {s.n_samples} labeled ({s.advanced} advanced · {s.rejected} rejected) · trust {pct(s.alpha)}
                </div>
              </div>
              <span style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: s.scope_type === 'company' ? '#F05000' : '#005A80', background: s.scope_type === 'company' ? '#EDE9FE' : '#E0F2FE', borderRadius: 999, padding: '0.15rem 0.5rem' }}>
                {s.scope_type === 'company' ? 'company override' : 'role default'}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
              {DIM_ORDER.map((d) => {
                const lw = s.weights?.[d] || 0;
                const pw = s.preset?.[d] || 0;
                const delta = lw - pw;
                const up = delta > 0.005, down = delta < -0.005;
                return (
                  <div key={d} data-testid={`learned-weight-${d}`} style={{ border: '1px solid #EEF2FF', borderRadius: 10, padding: '0.6rem 0.75rem', background: '#F8FAFF' }}>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#334155' }}>{DIM_LABELS[d]}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem', marginTop: '0.2rem' }}>
                      <span style={{ fontSize: '1.05rem', fontWeight: 800, fontFamily: 'Cabinet Grotesk', color: '#002A38' }}>{pct(lw)}</span>
                      <span style={{ fontSize: '0.625rem', fontWeight: 700, color: up ? '#059669' : down ? '#DC2626' : '#94A3B8' }}>
                        {up ? '▲' : down ? '▼' : '='} {pct(pw)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
